// Hero.jsx
import React, { useEffect, useRef, useState } from "react";

/**
 * Single-file Hero slider component
 * - No external/global CSS required (styles injected via <style> tag)
 * - Slides in from right -> left using translateX (no dots)
 * - Left/right controls on both sides (desktop + mobile)
 * - Autoplay + swipe + keyboard support
 */

export default function Hero() {
  const images = [
    "https://supersteno.com/assetsa/img/004%20website%20banner-01.jpg",
    "https://supersteno.com/assetsa/img/004%20website%20banner-02.jpg",
    "https://supersteno.com/assetsa/img/004%20website%20banner-03.jpg",
    "https://supersteno.com/assetsa/img/004%20website%20banner-04.jpg",
    "https://supersteno.com/assetsa/img/004%20website%20banner-05.jpg",
    "https://supersteno.com/assetsa/img/004%20website%20banner-06.jpg",
    "https://supersteno.com/assetsa/img/004%20website%20banner-07.jpg",
    "https://supersteno.com/assetsa/img/004%20website%20banner-08.jpg",
  ];

  const [index, setIndex] = useState(0);
  const slidesRef = useRef(null);
  const autoplayRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const transitioning = useRef(false);

  // autoplay
  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startAutoplay() {
    stopAutoplay();
    autoplayRef.current = setInterval(() => goNext(), 4500);
  }
  function stopAutoplay() {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }
  function resetAutoplay() {
    stopAutoplay();
    startAutoplay();
  }

  // move prev/next
  function goPrev() {
    if (transitioning.current) return;
    transitioning.current = true;
    setIndex(i => {
      const next = (i - 1 + images.length) % images.length;
      return next;
    });
    resetAutoplay();
    // allow next transition
    setTimeout(() => (transitioning.current = false), 520);
  }
  function goNext() {
    if (transitioning.current) return;
    transitioning.current = true;
    setIndex(i => (i + 1) % images.length);
    resetAutoplay();
    setTimeout(() => (transitioning.current = false), 520);
  }

  // touch handlers for swipe
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = touchStartX.current;
  };
  const onTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const onTouchEnd = () => {
    const delta = touchStartX.current - touchEndX.current;
    const threshold = 40;
    if (delta > threshold) goNext();
    else if (delta < -threshold) goPrev();
  };

  // keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // compute translateX (%)
  const translate = `translateX(-${index * 100}%)`;

  return (
    <>
      <style>{`
/* Component-scoped hero styles (no global CSS required) */
.hero-wrapper {
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  position: relative;
  background: transparent;
  
}

/* slider area */
.slider-viewport {
  width: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
  position: relative;
}

/* slides row */
.slides {
  display: flex;
  width: 100%;
  transition: transform 520ms cubic-bezier(.2,.9,.2,1);
  will-change: transform;
}

/* each slide */
.slide {
  min-width: 100%;
  flex: 0 0 100%;
  display: block;
  position: relative;
}

/* slide image: desktop = cover (fills), mobile = contain to show more) */
.slide img {
  width: 100%;
  height: 75vh;
  object-fit: cover;
  object-position: center;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}

/* controls — big round arrows (visible desktop) */
.ctrl {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 40;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.45);
  color: #fff;
  font-size: 30px;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(0,0,0,0.18);
}
.ctrl:hover { background: rgba(0,0,0,0.65); transform: translateY(-50%) scale(1.03); }
.ctrl.left { left: 14px; }
.ctrl.right { right: 14px; }

/* mobile-side controls (smaller, visible on narrow screens) */
.mobile-ctrl {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 42;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  display: none;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.42);
  color: #fff;
  font-size: 22px;
  cursor: pointer;
}
.mobile-ctrl.left { left: 8px; }
.mobile-ctrl.right { right: 8px; }

/* left stacked small icons (kept if you want them on desktop) */
.stack-icons {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 38;
}
.stack-icons button {
  width: 40px; height:40px; border-radius:50%; border:none;
  background: rgba(0,0,0,0.45); color:#fff; cursor:pointer;
}

/* remove extra gap above/below by resetting container margins */
.hero-wrapper, .slider-viewport, .slides, .slide { margin: 0; padding: 0; margin-top:2px}

/* responsive tweaks */
@media (max-width: 880px) {
 .slide img { height: 52vh; object-fit: cover; object-position: center top; margin-top:20px}
  .ctrl { display: none; }            /* hide large arrows on mobile */
  .mobile-ctrl { display: flex; }     /* show mobile controls */
  .stack-icons { display: none; }     /* hide stacked icons on mobile */
}
@media (max-width: 420px) {
  .slide img { height: auto; max-height: 56vh; object-fit: contain; }
  .mobile-ctrl { width:36px; height:36px; font-size:18px; }
}
  @media (max-width: 425px) {
  .slide img { height: 33vh; max-height: 56vh; object-fit: contain; margin-top:30px}
  .mobile-ctrl { width:36px; height:36px; font-size:18px; }
}

/* Accessibility focus styles */
.ctrl:focus, .mobile-ctrl:focus, .stack-icons button:focus {
  outline: 3px solid rgba(255,184,0,0.28);
  outline-offset: 2px;
}
      `}</style>

      <div
        className="hero-wrapper"
        onMouseEnter={stopAutoplay}
        onMouseLeave={startAutoplay}
      >
        <div
          className="slider-viewport"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          role="region"
          aria-label="Hero image slider"
        >
          <div
            ref={slidesRef}
            className="slides"
            style={{ transform: translate }}
          >
            {images.map((src, i) => (
              <div className="slide" aria-hidden={i !== index} key={i}>
                <img src={src} alt={`Banner ${i + 1}`} draggable="false" />
              </div>
            ))}
          </div>

          {/* Desktop big arrows (visible on wide screens) */}
          <button
            className="ctrl left"
            onClick={() => { goPrev(); }}
            aria-label="Previous slide"
            title="Previous"
          >
            ‹
          </button>
          <button
            className="ctrl right"
            onClick={() => { goNext(); }}
            aria-label="Next slide"
            title="Next"
          >
            ›
          </button>

          {/* Mobile side arrows (visible on mobile) */}
          <button
            className="mobile-ctrl left"
            onClick={() => { goPrev(); }}
            aria-label="Previous slide"
            title="Previous"
          >
            ‹
          </button>
          <button
            className="mobile-ctrl right"
            onClick={() => { goNext(); }}
            aria-label="Next slide"
            title="Next"
          >
            ›
          </button>

      
        </div>
      </div>
    </>
  );
}
